import { CommonModule } from '@angular/common'
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withJsonpSupport } from '@angular/common/http'
import { CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, NgModule } from '@angular/core'
import { ReactiveFormsModule } from '@angular/forms'
import { BrowserModule } from '@angular/platform-browser'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'
import { RouteReuseStrategy } from '@angular/router'
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin'
import { NgxsStoragePluginModule, SESSION_STORAGE_ENGINE } from '@ngxs/storage-plugin'
import { NgxsModule } from '@ngxs/store'
import { SpOverlayModule } from '@sp-common/saraplus-ngx-common'
import { SaraPlusFormModule } from '@sp-forms/saraplus-ngx-forms'
import { PdfViewerModule } from 'ng2-pdf-viewer'
import { DialogService } from 'primeng/dynamicdialog'
import { environment } from './../environments/environment'
import { AppRoutingModule } from './app-routing.module'
import { AppComponent } from './app.component'
import { CountdownDialogComponent } from './common/components/countdowndialog/countdowndialog.component'
import { CountdownTimerComponent } from './common/components/countdowntimer/countdown-timer.component'
import { EncryptDecryptComponent } from './common/components/encrypt-decrypt/encrypt-decrypt.component'
import { SpinnerComponent } from './common/components/spinner/spinner.component'
import { AuthHeaderInterceptor } from './common/interceptors/auth-header.interceptor'
import { ApiStateInterceptor } from './common/interceptors/generic-api-state.interceptor'
import { HttpErrorInterceptor } from './common/interceptors/http-error.interceptor'
import { LoadingIndicatorInterceptor } from './common/interceptors/loading-indicator.interceptor'
import { PayloadEncryptionInterceptor } from './common/interceptors/payload-encryption.interceptor'
import { AppBaseModule } from './common/modules/app-base.module'
import { AppLayoutModule } from './common/modules/app-layout/app-layout.module'
import { PrimeNGModule } from './common/modules/primeng.module'
import { AppRouteReuseStrategy } from './common/services/app-reroute-strategy'
import { CountdownTimerService } from './common/services/countdown-timer.service'
import { DataService } from './common/services/data.service'
import { ExternalDataService } from './common/services/external-data.service'
import { GenericState } from './common/store/generic-state/generic.state'
import { AppState } from './common/store/state/app/app.state'
import { LoginFlowState } from './common/store/state/login-flow/login-flow.state'
import { UserState } from './common/store/state/user/user.state'
import { AnalyticsModule } from './features/analytics/analytics.module'
import { CreateNewPasswordComponent } from './features/identity/forgot-password/create-new-password/create-new-password.component'
import { ForgotPasswordComponent } from './features/identity/forgot-password/forgot-password.component'
import { UserManagementComponent } from './features/identity/user-management/user-management.component';
import { AddUserComponent } from './features/identity/user-management/add-user/add-user.component';
import { TemporaryPasscodeComponent } from './features/identity/forgot-password/temporary-passcode/temporary-passcode.component'
import { LoginComponent } from './features/identity/login/login.component'
import { MfaCollectionComponent } from './features/identity/mfa/mfa-collection/mfa-collection.component'
import { MfaSelectionComponent } from './features/identity/mfa/mfa-selection/mfa-selection.component'
import { TotpSetupComponent } from './features/identity/mfa/totp-setup/totp-setup.component'
import { TotpVerifyComponent } from './features/identity/mfa/totp-verify/totp-verify.component'
import { QRCodeComponent } from 'angularx-qrcode'
import { MfaVerificationDialogComponent } from './features/identity/mfa/mfa-verification-dialog/mfa-verification-dialog.component'
import { MfaVerificationComponent } from './features/identity/mfa/mfa-verification/mfa-verification.component'
import { MyProfileComponent } from './features/identity/my-profile/my-profile.component'
import { SecurityQaComponent } from './features/identity/security-qa/security-qa.component'
import { UserCheckinComponent } from './features/identity/user-checkin/user-checkin.component'
import { WelcomeComponent } from './features/identity/welcome/welcome.component'
import { CalendarComponent } from './common/components/calendar/calendar.component'
import { SlotBookingComponent } from './common/components/booking/slot-booking.component'
import { ActionBarComponent } from './common/components/action-bar/action-bar.component'
import { GlobalErrorHandler } from './common/handlers/global-error.handler'
import { OrderStepGuard } from './common/guards/order-step.guard';
import { SamlAuthComponent } from './features/identity/saml-auth/saml-auth.component';
import { SsoCallbackComponent } from './features/identity/sso-callback/sso-callback.component';
import { InputOtpFixDirective } from './common/directives/input-otp-fix.directive'
import { CustomerSearchComponent } from './features/customer-search/customer-search.component'
import { CustomerLookupComponent } from './features/customer-search/customer-lookup/customer-lookup.component';
import { LogoutComponent } from './features/identity/logout/logout.component';
import { LicAuthComponent } from './features/identity/lic-auth/lic-auth.component'

@NgModule({
    declarations: [
        AppComponent,
        CountdownDialogComponent,
        CountdownTimerComponent,
        EncryptDecryptComponent,
        SpinnerComponent,
        LoginComponent,
        ForgotPasswordComponent,
        TemporaryPasscodeComponent,
        CreateNewPasswordComponent,
        MfaCollectionComponent,
        MfaVerificationComponent,
        MfaSelectionComponent,
        TotpSetupComponent,
        TotpVerifyComponent,
        SecurityQaComponent,
        WelcomeComponent,
        MyProfileComponent,
        MfaVerificationDialogComponent,
        UserCheckinComponent,
        SamlAuthComponent,
        SsoCallbackComponent,
        UserManagementComponent,
        AddUserComponent,
        CustomerSearchComponent,
        CustomerLookupComponent,
        LogoutComponent,
        LicAuthComponent
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    bootstrap: [AppComponent],
    imports: [AnalyticsModule,
        QRCodeComponent,
        AppBaseModule,
        AppLayoutModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        BrowserModule,
        NgxsModule.forRoot([UserState, AppState, LoginFlowState, GenericState], { developmentMode: !environment.production }),
        NgxsReduxDevtoolsPluginModule.forRoot(),
        NgxsStoragePluginModule.forRoot({
            keys: [{
                key: UserState,
                engine: SESSION_STORAGE_ENGINE
            }, {
                key: AppState,
                engine: SESSION_STORAGE_ENGINE
            }, {
                key: LoginFlowState,
                engine: SESSION_STORAGE_ENGINE
            },
            {
                key: GenericState,
                engine: SESSION_STORAGE_ENGINE
            }]
        }),
        PrimeNGModule,
        ReactiveFormsModule,
        PdfViewerModule,
        SaraPlusFormModule,
        SpOverlayModule,
        CommonModule,
        CalendarComponent,
        SlotBookingComponent,
        ActionBarComponent,
        InputOtpFixDirective],
    providers: [
        ExternalDataService,
        DialogService,
        CountdownTimerService,
        { provide: HTTP_INTERCEPTORS, useClass: AuthHeaderInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: PayloadEncryptionInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: LoadingIndicatorInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: ApiStateInterceptor, multi: true },
        { provide: RouteReuseStrategy, useClass: AppRouteReuseStrategy },
        DataService,
        provideHttpClient(withInterceptorsFromDi(), withJsonpSupport()),
        {
            provide: ErrorHandler,
            useClass: GlobalErrorHandler
        },
        OrderStepGuard
    ]
})
export class AppModule { }
